import { useState, useEffect } from 'react'

import { TextInput, CloseButton } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import dayjs from 'dayjs'

/**
 * DateInput component that combines a text input with auto-formatting to MM/DD/YYYY
 * and a visual DatePicker. Both components are synchronized and populate with the
 * current value when the component mounts.
 */

interface DateInputProps {
  initialValue?: Date | null
  onDateChange: (date: Date | null) => void
  placeholder?: string
  error?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

// Helper function to format input as MM/DD/YYYY
const formatDateInput = (input: string) => {
  // Remove all non-numeric characters
  const numbers = input.replace(/\D/g, '')

  // Apply formatting based on length
  if (numbers.length <= 2) {
    return numbers
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(
      4,
      8
    )}`
  }
}

// Helper function to parse MM/DD/YYYY format
const parseDate = (input: string) => {
  if (!input || input.trim() === '') return null

  const trimmed = input.trim()

  // Only accept MM/DD/YYYY format
  const parsed = dayjs(trimmed, 'MM/DD/YYYY', true)
  if (parsed.isValid()) {
    return parsed.toDate()
  }

  return null
}

// Helper function to validate MM/DD/YYYY format
const validateDateInput = (input: string) => {
  if (!input || input.trim() === '') return { isValid: true, error: '' }

  // Check if input matches MM/DD/YYYY pattern
  const datePattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/

  if (!datePattern.test(input)) {
    return {
      isValid: false,
      error: 'Please enter date in MM/DD/YYYY format',
    }
  }

  const date = parseDate(input)
  if (!date) {
    return {
      isValid: false,
      error: 'Invalid date. Please check month, day, and year values',
    }
  }

  return { isValid: true, error: '' }
}

// Helper function to format Date to MM/DD/YYYY string
const formatDateToString = (date: Date | null) => {
  if (!date) return ''
  return dayjs(date).format('MM/DD/YYYY')
}

const DateInput = ({
  initialValue = null,
  onDateChange,
  placeholder = 'MM/DD/YYYY',
  size = 'xs',
}: DateInputProps) => {
  const [dateInputValue, setDateInputValue] = useState('')
  const [dateInputError, setDateInputError] = useState('')
  const [currentDateValue, setCurrentDateValue] = useState<Date | null>(null)
  const [calendarDate, setCalendarDate] = useState<Date | undefined>(undefined)

  // Initialize both text input and current date value when component mounts or initialValue changes
  useEffect(() => {
    const formattedDate = formatDateToString(initialValue)
    setDateInputValue(formattedDate)
    setCurrentDateValue(initialValue)
    setCalendarDate(initialValue || undefined)
    setDateInputError('')
  }, [initialValue])

  // Handle input changes with auto-formatting and real-time validation
  const handleDateInputChange = (value: string) => {
    // Auto-format the input as MM/DD/YYYY
    const formattedValue = formatDateInput(value)
    setDateInputValue(formattedValue)

    // Only validate if input looks complete (10 characters for MM/DD/YYYY)
    if (formattedValue.length === 10) {
      const validation = validateDateInput(formattedValue)
      setDateInputError(validation.error)

      // If valid, update current date value for real-time DatePicker sync
      if (validation.isValid) {
        const parsed = parseDate(formattedValue)
        setCurrentDateValue(parsed)
        setCalendarDate(parsed || undefined)
        onDateChange(parsed)
      }
    } else {
      setDateInputError('')
    }
  }

  // Handle clear button click
  const handleClear = () => {
    setDateInputValue('')
    setDateInputError('')
    setCurrentDateValue(null)
    setCalendarDate(undefined)
    onDateChange(null)
  }

  // Handle DatePicker changes and sync with text input
  const handleDatePickerChange = (date: Date | null) => {
    // Update the text input to reflect the DatePicker selection
    const formattedDate = formatDateToString(date)
    setDateInputValue(formattedDate)
    setCurrentDateValue(date)
    setCalendarDate(date || undefined)
    setDateInputError('')

    // Call the parent's onChange
    onDateChange(date)
  }

  return (
    <div className="mb-2">
      <TextInput
        size={size}
        placeholder={placeholder}
        value={dateInputValue}
        onChange={(e) => handleDateInputChange(e.target.value)}
        error={dateInputError}
        className="mb-1"
        maxLength={10}
        rightSection={
          dateInputValue ? (
            <CloseButton size="sm" onClick={handleClear} />
          ) : null
        }
      />
      <div className="mb-2 text-xs text-doubleNickel-gray-500">
        Type date above or use picker below:
      </div>
      <DatePicker
        size={size}
        value={currentDateValue}
        date={calendarDate}
        onDateChange={setCalendarDate}
        onChange={handleDatePickerChange}
      />
    </div>
  )
}

export default DateInput
